import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import './home.css';
import { useTranslation } from "react-i18next";
import { GoCheck } from 'react-icons/go';
import { fetchAllSurveyDataApi, getInstitutions, registerUsersBulk, updateApproveStatus } from "../../services/institutions";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
import { TfiImport } from "react-icons/tfi";
import { CiExport } from "react-icons/ci";
import { FiPlus, FiSearch } from "react-icons/fi";
import { Modal, ModalHeader, ModalBody, ModalFooter, Table } from "reactstrap";
import LoadingScreen from "src/Loading/LoadingScreen";
import { FaCertificate, FaRegBuilding } from "react-icons/fa";
import { BarChart3, ChevronDown, ChevronUp } from "lucide-react";
import InstitutionMissionCharts from "./InstitutionMissionCharts";
import UploadTemplateModal from "../../components/Modals/UploadTemplateModal";
import { useUploadTemplate, uploadConfigs } from "src/hook/useUploadTemplate";
import InstitutionTemplateContent from "src/components/Modals/InstitutionTemplateContent";
import { useSearchQuery } from "src/hook/useSearchQuery";
import authService from "src/services/authService";
import LoadingButton from "../../components/Buttons/LoadingButton";

// Debounced search like Announcement.tsx (no persistence)

const InstitutionSupHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const perPage = 9;
  const [pageNo, setPageNo] = useState(0);
  const [institutions, setInstitutions] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState<{ [key: string]: boolean }>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCertificates, setSelectedCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>("");
  const [availableSurveys, setAvailableSurveys] = useState<any[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [isApproving, setIsApproving] = useState(false);
  /** When API scopes the list to direct child tenants only (parent org view). */
  const [listScope, setListScope] = useState<"all" | "children">("all");
  /**
   * Field operations dashboard collapse state. We default to expanded for
   * parent-org users (`listScope === 'children'`), since monitoring child
   * organizations like CHUM is the whole point of that login. For other
   * roles we keep the panel collapsed so the institution list still owns
   * the first viewport.
   */
  const [showFieldOps, setShowFieldOps] = useState<boolean>(false);
  useEffect(() => {
    if (listScope === "children") setShowFieldOps(true);
  }, [listScope]);

  const { searchQuery, searchResultType } = useSearchQuery();
  const { isModalOpen, openModal, closeModal, modalConfig, handleUpload } = useUploadTemplate(
    (files: FileList | null) => {
      if (files && files.length > 0) {
        const file = files[0];
        handleImportAndUpload(file);
      }
    },
    uploadConfigs.institutions
  );

  const onImportExcelClick = (): void => {
    openModal();
  };

  const updateApprovalStatus = async (
    tenantId: string,
    desiredApproved: boolean,
    surveyId?: string
  ) => {
    setIsButtonDisabled((prev) => ({ ...prev, [tenantId]: true }));

    try {
      const result = await updateApproveStatus(tenantId, desiredApproved, surveyId);
      toast.success("Tenant status updated successfully");
      fetchInstitutions();
    } catch (error: any) {
      toast.error(error.message || "Failed to update tenant status");
    } finally {
      setIsButtonDisabled((prev) => ({ ...prev, [tenantId]: false }));
      setShowSurveyModal(false);
      setSelectedSurveyId("");
      setSelectedTenantId("");
    }
  };

  const handleApprovalClick = async (tenantId: string, approved: boolean) => {
    if (approved) {
      // Unapprove and send tenantId as surveyId per backend expectations
      await updateApprovalStatus(tenantId, false, tenantId);
    } else {
      setSelectedTenantId(tenantId);
      setShowSurveyModal(true);

      try {
        const surveys :any= await fetchAllSurveyDataApi();
        setAvailableSurveys(surveys.data || []);
      } catch (error: any) {
        toast.error("Failed to load surveys");
        setAvailableSurveys([]);
      }
    }
  };

  const fetchInstitutions = async (searchQuery: string = "") => {
    //console.log('fetchInstitutions called with searchQuery:', searchQuery);
    setLoading(true);
    try {
      const response = await getInstitutions(searchQuery, perPage, pageNo);
      //console.log('fetchInstitutions API response:', response);
      if (response.success) {
        setInstitutions(response.data);
        setTotalPages(response.totalPages);
        if (response.listScope === "children" || response.listScope === "all") {
          setListScope(response.listScope);
        }
      }
    } catch (error: any) {
      toast.error(error.message);
      //console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExampleExcel = () => {
    const exampleData = [
      {
        tenantName: 'Test Corporation',
        industry: 'Technology',
        contactInfo: 'contact@testcorp.com',
        firstName: 'John',
        lastName: 'Doe',
        username: 'johndoe',
        email: 'john.doe@testcorp.com',
        address: '123 Main Street, Cairo',
        phone: '01234567890',
        password: 'TestPass123!',
      },
      {
        tenantName: 'Sample Industries',
        industry: 'Manufacturing',
        contactInfo: 'info@sample.com',
        firstName: 'Jane',
        lastName: 'Smith',
        username: 'janesmith',
        email: 'jane.smith@sample.com',
        address: '456 Industrial Avenue, Alexandria',
        phone: '01234567891',
        password: 'SamplePass456!',
      },
      {
        tenantName: 'Demo Company',
        industry: 'Finance',
        contactInfo: 'admin@demo.com',
        firstName: 'Ahmed',
        lastName: 'Hassan',
        username: 'ahmedhassan',
        email: 'ahmed.hassan@demo.com',
        address: '789 Business District, Giza',
        phone: '01234567892',
        password: 'DemoPass789!',
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(exampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Example');
    XLSX.writeFile(workbook, 'Organizations_Template.xlsx');
  };

  const handleImportAndUpload = (file: File) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error("No data found in the uploaded file.");
          return;
        }

        // Validate data structure before sending
        const requiredFields = ['tenantName', 'industry', 'contactInfo', 'firstName', 'lastName', 'username', 'email', 'address', 'phone', 'password'];
        const invalidRows: string[] = [];
        
        jsonData.forEach((row: any, index: number) => {
          const missingFields = requiredFields.filter(field => !row[field] || row[field].toString().trim() === '');
          if (missingFields.length > 0) {
            invalidRows.push(`Row ${index + 1}: Missing ${missingFields.join(', ')}`);
          }
        });

        if (invalidRows.length > 0) {
          toast.error(`Please fix the following issues:\n${invalidRows.slice(0, 5).join('\n')}${invalidRows.length > 5 ? '\n...' : ''}`);
          return;
        }

        const result = await registerUsersBulk(jsonData);

        if (result.isSuccess || result.success) {
          const count = result.totalInserted || jsonData.length;
          toast.success(`${count} users registered successfully`);
          fetchInstitutions();
        } else {
          const errorMessage = result.message || 'Failed to register data. Please check the data and try again.';
          toast.error(errorMessage);
        }
      } catch (error: any) {
        console.error("Upload Error:", error);
        const errorMessage = error?.response?.data?.message || error?.message || "Unexpected error occurred during upload.";
        toast.error(errorMessage);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleAssignCertificate = (id: any) => {
    navigate(`/assign-certifications/${id}`);
  };

  const handleFieldOperations = (card: { id: string; name: string }) => {
    const q = new URLSearchParams();
    if (card.name) q.set("name", card.name);
    navigate(`/institutionSup/child-operations/${encodeURIComponent(card.id)}?${q.toString()}`);
  };

  // Initial fetch and refetch on page change
  useEffect(() => {
    fetchInstitutions(debouncedSearchTerm);
  }, [perPage, pageNo, debouncedSearchTerm]);

  // Handle global search query - no persistence
  useEffect(() => {
    if (searchQuery && searchResultType === 'institutions') {
      setSearchTerm(searchQuery);
    }
  }, [searchQuery, searchResultType]);

  // Debounce search input by 1s
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm((searchTerm || '').trim());
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when typing
  useEffect(() => {
    setPageNo(0);
  }, [searchTerm]);

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleCertificatesClick = (certificates: any) => {
    setSelectedCertificates(certificates);
    toggleModal();
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const openFileInNewTab = async (fileUrl: string) => {
    try {
      const response = await authService.apiCall(fileUrl, {
        method: 'GET'
      });

      if (!response.ok) {
        toast("Failed to fetch file.", { type: "error" });
        return;
      }

      const blob = await response.blob();
      const fileObjectURL = URL.createObjectURL(blob);

      const newTab = window.open();
      if (newTab) {
        if (blob.type.startsWith("image/")) {
          newTab.document.write(`<img src="${fileObjectURL}" alt="Secure File" />`);
        } else {
          newTab.document.write(`
            <div style="text-align: center; margin: 20px;">
              <h2 style="color: #333;">${fileUrl.split('/').pop()}</h2>
              <a href="${fileObjectURL}" download target="_blank"
                 style="padding: 10px 20px; background-color: orange; color: white; border-radius: 5px; text-decoration: none;">
                Download File
              </a>
            </div>
          `);
        }
      }
    } catch (error: any) {
      toast(error?.message || "Error opening file", { type: "error" });
    }
  };

  if (loading) {
    return (
      <div className="loading-screen" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
        <div><LoadingScreen /></div>
      </div>
    );
  }

  const onOrgChartClick = (): void => {
    navigate("/organization-chart");
  };

  const onRefreshList = (): void => {
    fetchInstitutions(debouncedSearchTerm);
  };

  const pageCount = Math.max(totalPages, 1);

  return (
    <div className="inst-sup-page">

      <div className="inst-sup-control-card">
        <div className="inst-sup-toolbar">
          <div className="inst-sup-search-wrap">
            <FiSearch className="inst-sup-search-icon" size={18} aria-hidden />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={String(t("institution.Search institutions"))}
              className="inst-sup-search-input"
              aria-label={String(t("institution.Search institutions"))}
            />
            {searchTerm ? (
              <button type="button" className="inst-sup-clear-search" onClick={handleClearSearch}>
                {t("institution.clear_search")}
              </button>
            ) : null}
          </div>
          <div className="inst-sup-actions" role="group" aria-label={String(t("institution.institutions"))}>
            <button type="button" className="inst-btn inst-btn-outline" onClick={downloadExampleExcel}>
              <CiExport size={18} aria-hidden />
              <span>{t("Standards_Page.download_excel_template")}</span>
            </button>
            <button type="button" className="inst-btn inst-btn-outline" onClick={onImportExcelClick}>
              <TfiImport size={16} aria-hidden />
              <span>{t("Standards_Page.import_excel")}</span>
            </button>
          
          </div>
        </div>
      </div>

      {listScope === "children" ? (
        <div className="inst-sup-child-scope-banner mb-2" role="status">
          {t("institution.child_scope_banner")}
        </div>
      ) : null}

      {/*
        Field operations dashboard — embedded so the parent org (e.g. Madinet
        Masr) can monitor live mission analytics from the CHUM mobile app
        right on the institution list page, instead of drilling into each
        child one at a time. The chart component is collapsible so users
        who came here for the institution roster aren't pushed below the
        fold by a 6-KPI / 5-chart panel they don't need.

        InstitutionMissionCharts already handles:
          - 403 (non-supervisor) → friendly "no access" hint
          - empty range / no data → empty-state message
          - date range picker
        so we don't replicate any of that here.
      */}
      <section className="inst-sup-field-ops-panel mb-3">
        <button
          type="button"
          className="inst-sup-field-ops-toggle"
          onClick={() => setShowFieldOps((v) => !v)}
          aria-expanded={showFieldOps}
          aria-controls="inst-sup-field-ops-body"
        >
          <span className="inst-sup-field-ops-toggle-icon" aria-hidden>
            <BarChart3 size={18} />
          </span>
          <span className="inst-sup-field-ops-toggle-text">
            <span className="inst-sup-field-ops-toggle-title">
              {t("institution.field_ops_dashboard_title")}
            </span>
            <span className="inst-sup-field-ops-toggle-subtitle">
              {t("institution.field_ops_dashboard_subtitle")}
            </span>
          </span>
          <span className="inst-sup-field-ops-toggle-chevron" aria-hidden>
            {showFieldOps ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </span>
          <span className="visually-hidden">
            {showFieldOps
              ? t("institution.field_ops_dashboard_hide")
              : t("institution.field_ops_dashboard_show")}
          </span>
        </button>
        {showFieldOps ? (
          <div id="inst-sup-field-ops-body" className="inst-sup-field-ops-body">
            <InstitutionMissionCharts />
          </div>
        ) : null}
      </section>

      {institutions.length > 0 ? (
        <div className="inst-sup-meta-strip">
          <div className="inst-sup-records-bar">
            <span className="inst-sup-records-label">{t("institution.toolbar_total")}</span>
            <span className="inst-sup-records-value">{institutions.length}</span>
            <button type="button" className="inst-sup-refresh-inline" onClick={onRefreshList} title={String(t("institution.refresh_list"))}>
              ↻
            </button>
          </div>
        </div>
      ) : null}

      <div className="inst-sup-panel">
        {institutions.length > 0 ? (
          <div className="inst-sup-grid">
            {institutions.map((card: any) => (
              <article className="inst-sup-card" key={card.id}>
                <div className="inst-sup-card-head">
                  <div className="inst-sup-card-icon" aria-hidden>
                    <FaRegBuilding size={20} />
                  </div>
                  <h2 className="inst-sup-card-title">{card.name}</h2>
                </div>

                <div className="inst-sup-stats">
                  <div className="inst-sup-stat">
                    <span className="inst-sup-stat-label">{t("institution.compliant")}</span>
                    <span className="inst-sup-stat-value inst-sup-stat-ok">{card.complianceCount}</span>
                  </div>
                  <div className="inst-sup-stat">
                    <span className="inst-sup-stat-label">{t("institution.non_compliant")}</span>
                    <span className="inst-sup-stat-value">{card.nonComplianceCount}</span>
                  </div>
                  <div className="inst-sup-stat">
                    <span className="inst-sup-stat-label">{t("institution.registered_on")}</span>
                    <span className="inst-sup-stat-value inst-sup-stat-muted">
                      {new Date(card.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="inst-sup-stat inst-sup-stat-wide">
                    <span className="inst-sup-stat-label">{t("institution.certificates_title")}</span>
                    <span className="inst-sup-stat-value">
                      {card.certificates.length === 0
                        ? t("institution.no_certificates")
                        : `${card.certificates.length} ${t("institution.certificate_plural")}`}
                    </span>
                    {card.certificates.length > 0 ? (
                      <button
                        type="button"
                        className="inst-sup-linkish"
                        onClick={() => handleCertificatesClick(card.certificates)}
                      >
                        {t("institution.view_certificates")}
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="inst-sup-card-actions">
                  <button
                    type="button"
                    className={`inst-sup-foot-btn ${card.approved ? "is-approved" : "is-pending"}`}
                    onClick={() => handleApprovalClick(card.id, card.approved)}
                    disabled={isButtonDisabled[card.id]}
                  >
                    {isButtonDisabled[card.id] ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                        {t("institution.processing")}
                      </>
                    ) : card.approved ? (
                      <>
                        {t("institution.approved")}
                        <GoCheck className="ms-2" size={16} aria-hidden />
                      </>
                    ) : (
                      t("institution.not_approved")
                    )}
                  </button>
                  <button
                    type="button"
                    className="inst-sup-foot-btn inst-sup-foot-btn-secondary"
                    onClick={() => handleAssignCertificate(card.id)}
                  >
                    <FaCertificate className="me-2" aria-hidden />
                    {t("institution.assign_certificate")}
                  </button>
                  <button
                    type="button"
                    className="inst-sup-foot-btn inst-sup-foot-btn-ops"
                    onClick={() => handleFieldOperations(card)}
                  >
                    <BarChart3 className="me-2" size={16} aria-hidden />
                    {t("institution.field_operations_reports")}
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="inst-sup-empty" role="status">
            <h5 className="inst-sup-empty-title">{t("institution.no_institutions_found")}</h5>
          </div>
        )}
      </div>

      {institutions.length > 0 ? (
        <nav className="inst-sup-pagination" aria-label={String(t("Governance_Page.page"))}>
          <button
            type="button"
            className="inst-sup-page-nav"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 0))}
            disabled={pageNo === 0}
          >
            ‹ {t("institution.pagination_previous")}
          </button>
          <span className="inst-sup-page-current" aria-current="page">
            {pageNo + 1}
          </span>
          <button
            type="button"
            className="inst-sup-page-nav"
            onClick={() => setPageNo((prev) => Math.min(prev + 1, pageCount - 1))}
            disabled={pageNo >= pageCount - 1}
          >
            {t("institution.pagination_next")} ›
          </button>
        </nav>
      ) : null}

      <Modal isOpen={modalOpen} toggle={toggleModal} size="xl" className="wide-modal">
        <ModalHeader toggle={toggleModal} className="bg-light border-bottom">
          <h5 className="mb-0">{t("institution.certificates_title")}</h5>
        </ModalHeader>
        <ModalBody>
          <div className="table-responsive">
            <Table className="table-hover">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '70%' }}>{t("institution.image")}</th>
                  <th style={{ width: '30%' }}>{t("institution.name")}</th>
                </tr>
              </thead>
              <tbody>
                {selectedCertificates.map((certificate: any, index: any) => (
                  <tr key={index}>
                    <td>
                      <div className="d-flex justify-content-center">
                        <img
                          src={process.env.REACT_APP_API_URL + certificate?.src + "?public=true"}
                          alt={certificate?.certificate?.name}
                          className="img-fluid"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '300px',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                          onClick={() => openFileInNewTab(process.env.REACT_APP_API_URL + certificate?.src + "?current=certificate")}
                        />
                      </div>
                    </td>
                    <td className="align-middle">
                      <h6 className="mb-0 text-dark">{certificate?.certificate?.name}</h6>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={showSurveyModal}
        toggle={() => setShowSurveyModal(false)}
        centered
        size="lg"
        className="inst-sup-modal inst-sup-survey-approve-modal"
      >
        <ModalHeader toggle={() => setShowSurveyModal(false)} className="inst-sup-modal-header">
          {t("institution.select survey to approve")}
        </ModalHeader>
        <ModalBody className="inst-sup-modal-body">
          <div className="inst-sup-modal-field">
            <label className="inst-sup-modal-label" htmlFor="surveySelect">
              {t("institution.select survey")}
            </label>
            <select
              id="surveySelect"
              className="form-select inst-sup-modal-select"
              value={selectedSurveyId}
              onChange={(e) => setSelectedSurveyId(e.target.value)}
              aria-label={String(t("institution.select survey"))}
            >
              <option value="">{t("institution.select survey")}</option>
              {availableSurveys.length > 0 ? (
                availableSurveys.map((survey) => (
                  <option key={survey.id} value={survey.id}>
                    {survey.title}
                  </option>
                ))
              ) : (
                <option disabled>{t("institution.no surveys available")}</option>
              )}
            </select>
          </div>
        </ModalBody>
        <ModalFooter className="inst-sup-modal-footer inst-sup-modal-footer--actions">
          <button type="button" className="inst-sup-modal-btn inst-sup-modal-btn--outline" onClick={() => setShowSurveyModal(false)}>
            {t("institution.Cancel")}
          </button>
          <LoadingButton
            onClick={async () => {
              if (!selectedSurveyId) return;
              setIsApproving(true);
              try {
                await updateApprovalStatus(selectedTenantId, true, selectedSurveyId);
              } finally {
                setIsApproving(false);
              }
            }}
            className="inst-sup-modal-btn inst-sup-modal-btn--primary"
            variant="success"
            disabled={!selectedSurveyId || isApproving}
            loadingText={String(t("institution.processing") || "")}
          >
            {t("institution.Confirm Approval")}
          </LoadingButton>
        </ModalFooter>
      </Modal>

      <UploadTemplateModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onUpload={handleUpload}
        title={modalConfig.title}
        description={modalConfig.description}
        acceptedFileTypes={modalConfig.acceptedFileTypes}
        maxFileSize={modalConfig.maxFileSize}
        showFileInput={modalConfig.showFileInput}
        customTemplateContent={<InstitutionTemplateContent />}
      />
    </div>
  );
};

export default InstitutionSupHome;